import classNames from 'classnames';
import { FORM_ERROR } from 'final-form';
import type { FC, PointerEvent } from 'react';
import { memo, useCallback, useMemo, useRef, useState } from 'react';
import type { FormRenderProps, FormSpyRenderProps } from 'react-final-form';
import { Form, FormSpy } from 'react-final-form';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';

import { ButtonV3Icon } from '../../components/Button/ButtonV3Icon';
import { EditorField } from '../../components/FinalForm/EditorField';
import { EditorFieldSimple } from '../../components/FinalForm/EditorFieldSimple';
import { failureToast } from '../../components/Toast';
import { frontConfig } from '../../config';
import type { GraphEditorInstanceRef } from '../../editor/editor-model';
import {
  startOpenEditorStartFocusWorkaround,
  unserializeEditorDbEntry,
  updateContentAndEmbedWithEditorjsOutput,
} from '../../editor/editorjs/editorjs-utils';
import type { AuthPermissions } from '../../hooks/usePermissions';
import { usePermissions } from '../../hooks/usePermissions';
import { useWebsite } from '../../hooks/useWebsite';
import { ReactComponent as IconCloseCircle } from '../../icons/icon-x-circle.svg';
import { uploadImage } from '../../services/api/uploadFile';
import type { Message, Website } from '../../types/api';
import { handleError } from '../../utils/common-utils';
import { stripHtml, textToHtml } from '../../utils/dom';
import { getNbImagesMax } from '../../utils/embed';
import { useLoginWithPromise } from '../../utils/graphlogin/AuthWithPromise-context';
import { NoSSR } from '../../utils/ssr';
import { maxLength, minEditorLength } from '../../utils/validation';
import { isMobile } from '../../utils/web';
import type { CreateOrUpdateMessageVariablesSmall } from '../api/message';
import type { SerializedValue } from '../ThreadMessage/useSerializeValue';
import { serializedToExportedString, useSerializeValue } from '../ThreadMessage/useSerializeValue';
import { CguStatement } from './CguStatement';
import type { FormDataType } from './DisplayWhenValid';
import { EditorSubmitButton } from './EditorSubmitButton';
import { ThreadEditorToolbar } from './ThreadEditorToolbar';

// TODO move to Thread types
export type EditorCloseCallback = (result?: Message) => void;

export type EditorPropTypes = {
  baseId: string;
  className?: string;
  placeholder?: string;
  onSubmitAnswer: (elt: CreateOrUpdateMessageVariablesSmall) => Promise<void>;
  onCancelAnswer?: VoidFunction;
  message?: Message;
  editorRef?: GraphEditorInstanceRef;
  hideCancelButton?: boolean;
  hideIfEmpty?: boolean;
  noBorder?: boolean;
  withAvatar?: boolean;
};

export const threadEditorMessages = defineMessages({
  alert: {
    defaultMessage:
      '<b>Alerte:</b> Ce compte Utilisateur ne peut pas publier de commentaire pour non-respect des {cgu}.',
    id: '4XlCNx',
  },
  tooShort: {
    defaultMessage: 'Votre message est trop court ({min} caractères minimum)',
    id: 'tuIYAm',
  },
  tooLong: { defaultMessage: 'Votre message est trop long', id: 'tHR+WL' },
  genericError: {
    defaultMessage: "Une erreur est survenue lors de l'enregistrement de votre message",
    id: 'RjK1+o',
  },
  editorPlaceHolder: { defaultMessage: 'Entrez le contenu de votre message', id: 'AIrCD7' },
  imagesOnly: { defaultMessage: "L'upload est réservé aux images seulement", id: '9eX/pe' },
  maxEmbedFilesReached: {
    defaultMessage: 'Niveau maximal d’images autorisées atteint',
    id: '1bNb9g',
  },
  embedFilesDisabled: { defaultMessage: "Upload d'images non autorisé", id: 'jzHkd/' },
  confirmClear: { defaultMessage: 'Supprimer le brouillon de message ?', id: 'xHVFzf' },
});

/**
 * Constants below should be returned by API
 */
const MAX_LENGTH = 10000;
const MIN_LENGTH = 3;

type ThreadFormErrorCompProps = FormSpyRenderProps<
  Record<string, any>,
  Partial<Record<string, any>>
>;

const ThreadFormErrorComp: FC<ThreadFormErrorCompProps> = memo(function ThreadFormErrorComp(p) {
  const { hasSubmitErrors, modifiedSinceLastSubmit, submitErrors } = p;
  if (!hasSubmitErrors || modifiedSinceLastSubmit) return null;
  const msgKey = submitErrors?.[FORM_ERROR] as keyof typeof threadEditorMessages;
  if (!threadEditorMessages[msgKey]) {
    handleError('submitErrors:', msgKey);
  }
  return (
    <span className="text-primary-base py-2 mt-3 font-bold block">
      {msgKey ? (
        <FormattedMessage
          {...(threadEditorMessages[msgKey] ?? threadEditorMessages.genericError)}
          values={{
            max: MAX_LENGTH,
            min: MIN_LENGTH,
          }}
        />
      ) : null}
    </span>
  );
});

const ERROR_SPY = (
  <FormSpy
    subscription={{
      submitErrors: true,
      hasSubmitErrors: true,
      modifiedSinceLastSubmit: true,
    }}
    render={p => <ThreadFormErrorComp {...p} />}
  />
);

type ThreadEditorFormRawPropTypes = EditorPropTypes & {
  currentBan?: AuthPermissions['currentBan'];
  website: Partial<Website>;
  isAdmin: boolean;
  authUserId?: string;
  noBorder?: boolean;
  withAvatar?: boolean;
};

const EmptySubscription = {};

export const ThreadEditorFormRaw: FC<ThreadEditorFormRawPropTypes> = memo(
  function ThreadEditorFormRaw(p) {
    const {
      onSubmitAnswer: submit,
      onCancelAnswer,
      website,
      isAdmin,
      currentBan,
      baseId,
      editorRef: editorRefArg,
      hideCancelButton,
      ...editorFieldProps
    } = p;
    const { authUserId, message, placeholder } = p;

    // In case no ref is passed, we still need one internally, at least to make the clear function work.
    let editorRefInternal: typeof editorRefArg = useRef(null);
    const editorRef = editorRefArg || editorRefInternal;

    const { formatMessage } = useIntl();

    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [embedFilesCount, setEmbedFilesCount] = useState(0);

    const loginWithPromise = useLoginWithPromise();

    const [initialValueRef, serializeValue, clearValue] = useSerializeValue(baseId);

    const closeAndClearInternal = useCallback(() => {
      if (editorRef && editorRef.current) {
        editorRef.current.reset();
        // ref.current.blur(); // AOL: Why a blur? I commented.
        // Editorjs reset includes a focus().
      }
      clearValue();
    }, [clearValue, editorRef]);

    const focusEditorField = useCallback(() => {
      if (editorRef?.current) {
        startOpenEditorStartFocusWorkaround();
        editorRef.current?.focus();
      }
    }, [editorRef]);

    const validateSubmit = useCallback((formValues: string | undefined) => {
      let error;
      const minLengthError = minEditorLength(MIN_LENGTH)(formValues);
      const maxLengthError = maxLength(MAX_LENGTH)(formValues);

      if (minLengthError) error = 'tooShort';
      if (maxLengthError) error = 'tooLong';

      return error
        ? {
            [FORM_ERROR]: error,
          }
        : null;
    }, []);

    const useRawTextEditor = isMobile() && !frontConfig.enableExperimentalEditorjs;

    const onSubmit = useCallback(
      async (formValues: FormDataType): Promise<Record<string, string> | null> => {
        try {
          if (!authUserId) {
            await loginWithPromise();
          }
          setLoading(true);
          let { content, adminBoost, hiddenEmbeds } = formValues;

          let [contentHtml, embeds, hiddenEmbeds2] = updateContentAndEmbedWithEditorjsOutput(
            content,
            hiddenEmbeds,
          );
          hiddenEmbeds = hiddenEmbeds2;

          // On mobile, the editor is emitting raw text. We need to preserve line breaks.
          if (useRawTextEditor) {
            contentHtml = textToHtml(contentHtml);
          }

          const validationErrors = validateSubmit(contentHtml);
          if (validationErrors !== null) {
            return validationErrors;
          }

          /* const result = */ await submit({
            adminBoost,
            content: contentHtml || '',
            hiddenEmbeds,
            embeds,
          });

          closeAndClearInternal(/* result */);
          return null;
        } catch (error: any) {
          return { [FORM_ERROR]: error.message };
        } finally {
          setLoading(false);
        }
      },
      [
        authUserId,
        closeAndClearInternal,
        loginWithPromise,
        submit,
        useRawTextEditor,
        validateSubmit,
      ],
    );

    const upload = useCallback(
      async (file: File) => {
        const currentEditor = editorRef?.current;
        const { upload: uploadEnabled = false } = website?.graphdebate?.edition || {};
        const maxEmbedFiles = getNbImagesMax(website);

        if (!currentEditor || !uploadEnabled) return;

        if (maxEmbedFiles === 0) {
          failureToast(<FormattedMessage {...threadEditorMessages.embedFilesDisabled} />);
          return;
        }

        // embed files limit reached
        if (embedFilesCount === maxEmbedFiles) {
          failureToast(<FormattedMessage {...threadEditorMessages.maxEmbedFilesReached} />);
          return;
        }

        setUploading(true);

        try {
          const fileUrl = await uploadImage(file);
          currentEditor.addContent(fileUrl);
          setEmbedFilesCount(v => v + 1);
        } catch (error: any) {
          handleError(error);
          failureToast(
            <FormattedMessage
              {...(error.message in threadEditorMessages
                ? threadEditorMessages[error.message as keyof typeof threadEditorMessages]
                : threadEditorMessages.genericError)}
            />,
          );
        } finally {
          setUploading(false);
        }
      },
      [editorRef, embedFilesCount, website],
    );

    const decrementEmbedFiles = useCallback(() => {
      setEmbedFilesCount(v => Math.max(0, v - 1));
    }, []);

    let initialValue = useMemo<SerializedValue>(
      () => initialValueRef.current || unserializeEditorDbEntry(message?.content, message?.embeds),
      [initialValueRef, message?.content, message?.embeds],
    );

    if (useRawTextEditor) {
      initialValue = stripHtml(serializedToExportedString(initialValue));
    }

    const hasInitialValue = !!initialValue;

    const hasCancelButton =
      typeof onCancelAnswer === 'function' && !frontConfig.enableExperimentalEditorjs;

    const closeAndClearNoSubmit = useCallback(() => {
      if (
        editorRef.current?.isEmpty() ||
        confirm(formatMessage(threadEditorMessages.confirmClear))
      ) {
        closeAndClearInternal();
        onCancelAnswer?.();
      }
    }, [closeAndClearInternal, editorRef, formatMessage, onCancelAnswer]);

    const preventDefault = useCallback(
      (e: PointerEvent<HTMLAnchorElement | HTMLButtonElement>) => e.preventDefault(),
      [],
    );

    const renderForm = useCallback(
      ({ handleSubmit }: FormRenderProps<FormDataType>) => {
        const loading2 = loading || uploading;

        const editor = useRawTextEditor ? (
          <EditorFieldSimple
            {...editorFieldProps}
            name="content"
            ref={editorRef}
            placeholder={placeholder ?? formatMessage(threadEditorMessages.editorPlaceHolder)}
            loading={loading2}
            initialValue={initialValue}
            serializeValue={serializeValue}
            hasCancelButton={hasCancelButton}
          />
        ) : (
          <EditorField
            {...editorFieldProps}
            id={baseId}
            name="content"
            editorRef={editorRef}
            placeholder={
              placeholder ?? <FormattedMessage {...threadEditorMessages.editorPlaceHolder} />
            }
            inlineToolbar={website?.graphdebate?.edition?.formatting as boolean}
            onUpload={upload}
            onRemoveEmbed={decrementEmbedFiles}
            loading={loading2}
            onSubmit={handleSubmit}
            className="pr-9"
            initialValue={initialValue}
            serializeValue={serializeValue}
            // Switcht to true if the submit button is displayed below
            hasExternalSend={false}
            onCancelAnswer={!hideCancelButton ? closeAndClearNoSubmit : undefined}
            hasInitialValue={hasInitialValue}
          />
        );

        return (
          <NoSSR>
            <form
              onSubmit={handleSubmit}
              className="flex-1 min-w-0 min-h-0 flex flex-col text-base"
            >
              <div className="EditorField flex-1 min-w-0 min-h-0 flex relative">
                {frontConfig.enableExperimentalEditorjs ? (
                  editor
                ) : (
                  <div
                    className="cursor-text w-full flex"
                    onClick={focusEditorField}
                    role="button"
                    tabIndex={0}
                  >
                    {editor}
                  </div>
                )}

                {hasCancelButton && (
                  <div className="editorCloseBtn absolute top-1 right-1 z-[1]">
                    <div className="relative z-2">
                      <ButtonV3Icon
                        onClick={closeAndClearNoSubmit}
                        onPointerDown={preventDefault}
                        icon={<IconCloseCircle className="w-2 h-2" />}
                        variant="outlined"
                        radius="rounded-full"
                        className="text-gray-300 disabled:text-gray-250"
                        disabled={loading2}
                      />
                    </div>
                  </div>
                )}
              </div>

              <>
                {/* Disable the submit button here if using editorjs - it will be embedded in the toolbar */}
                {!frontConfig.enableExperimentalEditorjs && (
                  <div
                    className={classNames(
                      'flex text-gray-medium items-center pt-2',
                      frontConfig.enableExperimentalEditorjs
                        ? 'justify-end mr-1'
                        : 'justify-between',
                    )}
                  >
                    {!frontConfig.enableExperimentalEditorjs && (
                      <ThreadEditorToolbar
                        key={baseId}
                        baseId={baseId}
                        editorRef={editorRef}
                        setLoading={setLoading}
                      />
                    )}
                    <div className="flex items-center space-x-4">
                      <EditorSubmitButton
                        loading={loading2}
                        hasInitialValue={hasInitialValue}
                        isAdmin={isAdmin}
                      />
                    </div>
                  </div>
                )}
                <CguStatement
                  noBorder={editorFieldProps.noBorder}
                  hasInitialValue={hasInitialValue}
                />
                {ERROR_SPY}
              </>
            </form>
          </NoSSR>
        );
      },
      [
        loading,
        uploading,
        useRawTextEditor,
        editorFieldProps,
        editorRef,
        placeholder,
        formatMessage,
        initialValue,
        serializeValue,
        hasCancelButton,
        baseId,
        website?.graphdebate?.edition?.formatting,
        upload,
        decrementEmbedFiles,
        hideCancelButton,
        closeAndClearNoSubmit,
        hasInitialValue,
        focusEditorField,
        preventDefault,
        isAdmin,
      ],
    );

    return (
      <Form
        onSubmit={onSubmit}
        render={renderForm}
        subscription={EmptySubscription}
        initialValues={{
          content: initialValue,
          hiddenEmbeds: (message?.hiddenEmbeds ?? []) as string[],
        }}
      />
    );
  },
);

export const ThreadEditorForm: FC<EditorPropTypes> = props => {
  const website = useWebsite();
  const { isAdmin, currentBan, authUserId, isAuthDone } = usePermissions();

  // Editorjs has a bug with .focus() when there are multiple instances of the editor.
  // Discussion: https://github.com/codex-team/editor.js/issues/1503
  // To mitigate the issue on a basic use case like loading the page, which renders twice (auth in progress, then done), and recreates the ThreadEditorFormRaw (authUserId changes, cf the key below), we don't render the form as long as the auth process is not completed (regardless of whether the user is authenticated or not, but the check is finished).
  //
  if (!isAuthDone) return null;

  return (
    <ThreadEditorFormRaw
      {...props}
      // The key ensures two things:
      // 1. we fit a React constraint requiring a key in a list
      // 2. If authUserId changes, the component is recreated
      // 2. is important because the form decorators don't support a change of the function's reference. But with the current architecture, the function reference must change when the authUserId changes. So we recreate the whole form to avoid using the decorator of the old user.
      // If the decorator is removed, authUserId can be removed from the key.
      key={`${props.baseId}__${authUserId}`}
      website={website}
      isAdmin={isAdmin}
      currentBan={currentBan}
      authUserId={authUserId}
    />
  );
};

ThreadEditorForm.displayName = 'ThreadEditorForm';
